import type { CustomChartSerie } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { useMemo } from "react"
import { useMetrics } from "../../../data/useMetrics"
import { toDictionary } from "../../../utils/toDictionary"
import type { MetricConfiguration } from "../../../utils/trpc"
import type { TooltipOptions } from "../tt-ui-chart/Tooltip"

const medianAggregateMetrics = new Set([
  "transaction-fee-average",
  "block-time",
  "arpu",
  "afpu",
  "trading-volume-avg-per-trade-count",
  "trading-volume-avg-per-user",
])

function isAggregateMetric(metricConfig: MetricConfiguration) {
  return metricConfig.metric_id !== "price" && metricConfig.format !== "ratio"
}

export const isMedianMetric = (metricConfig: MetricConfiguration) => {
  return (
    metricConfig.format === "ratio" ||
    medianAggregateMetrics.has(metricConfig.slug)
  )
}

/** Tooltip options for a single-serie chart. Suspends until the metrics load. */
export function useChartTooltipOptions(chartSettings: Array<CustomChartSerie>) {
  const metrics = useMetrics()
  const chartSetting = chartSettings.length === 1 ? chartSettings[0] : undefined

  return useMemo(() => {
    if (!chartSetting) {
      return undefined
    }

    const metricConfig = toDictionary(metrics, "slug").get(chartSetting.metric)
    if (!metricConfig) {
      return undefined
    }

    // Percent-format metrics (e.g. asset_apy) can't be summed across assets;
    // show median instead so the bottom row stays informative.
    const isPercentMetric = metricConfig.format === "percent"

    const tooltipOpts: TooltipOptions = {
      sort: true,
      showTotal:
        isPercentMetric ||
        (isAggregateMetric(metricConfig) &&
          !isMedianMetric(metricConfig) &&
          chartSetting.display !== "percentage" &&
          !chartSetting.ratioMetric), // Hide total for ratio charts
      aggregation: isPercentMetric ? "median" : "sum",
      aggregationLabel: isPercentMetric ? "Median" : "Total",
      isLowerValueBetter: metricConfig.is_lower_value_better,
    }
    return tooltipOpts
  }, [chartSetting, metrics])
}
